import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api, useTenant } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import MissionsMap from '../components/MissionsMap';
import TenantFilter from '../components/TenantFilter';
import { 
  MapPin, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  FileCheck,
  Receipt,
  Loader2,
  Search,
  ArrowUpRight,
  Zap,
  Calendar,
  Filter,
  Download,
  Building2,
  Users
} from 'lucide-react';

const statusConfig = {
  criada: { label: 'Criadas', color: 'bg-blue-50 text-blue-600 border-blue-100', icon: AlertCircle, iconColor: 'text-blue-500' },
  aguardando_inicio: { label: 'Aguardando', color: 'bg-amber-50 text-amber-600 border-amber-100', icon: Clock, iconColor: 'text-amber-500' },
  em_andamento: { label: 'Em Andamento', color: 'bg-purple-50 text-purple-600 border-purple-100', icon: Loader2, iconColor: 'text-purple-500' },
  finalizada: { label: 'Finalizadas', color: 'bg-green-50 text-green-600 border-green-100', icon: CheckCircle, iconColor: 'text-green-500' },
  cancelada: { label: 'Canceladas', color: 'bg-red-50 text-red-600 border-red-100', icon: XCircle, iconColor: 'text-red-500' },
  aguardando_validacao: { label: 'Validação', color: 'bg-indigo-50 text-indigo-600 border-indigo-100', icon: FileCheck, iconColor: 'text-indigo-500' },
  aprovado_faturamento: { label: 'Aprovadas', color: 'bg-emerald-50 text-emerald-600 border-emerald-100', icon: Receipt, iconColor: 'text-emerald-500' },
  faturado: { label: 'Faturadas', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: CheckCircle, iconColor: 'text-slate-500' },
};

// Status principais para os cards (operacionais)
const mainStatuses = ['criada', 'aguardando_inicio', 'em_andamento', 'finalizada', 'cancelada'];

// Status secundários para ícones no topo (financeiros/administrativos)
const secondaryStatuses = ['aguardando_validacao', 'aprovado_faturamento', 'faturado'];

const activeStatuses = ['criada', 'aguardando_inicio', 'em_andamento'];

const DashboardPage = () => {
  const { token, user } = useAuth();
  const { selectedTenant } = useTenant();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [missions, setMissions] = useState([]);
  const [mapMissions, setMapMissions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [daysFilter, setDaysFilter] = useState(30);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [selectedMissionId, setSelectedMissionId] = useState(null);

  // Construir query params com tenant
  const getTenantParam = () => {
    if (user?.role === 'mestre' && selectedTenant && selectedTenant !== 'all') {
      return `tenant_cnpj=${selectedTenant}`;
    }
    return '';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tenantParam = getTenantParam();
        const separator = tenantParam ? '?' : '';
        
        const [statsData, missionsData, mapData, customersData, suppliersData] = await Promise.all([
          api.get(`/dashboard/stats${separator}${tenantParam}`, token),
          api.get(`/missions${separator}${tenantParam}`, token),
          api.get(`/dashboard/missions-map${separator}${tenantParam}`, token),
          api.get(`/customers${separator}${tenantParam}`, token),
          api.get(`/suppliers${separator}${tenantParam}`, token)
        ]);
        setStats(statsData);
        setMissions(missionsData);
        setMapMissions(mapData);
        setCustomers(customersData);
        setSuppliers(suppliersData);
      } catch (error) {
        toast.error('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, selectedTenant]);

  // Filtrar por período
  const filterByDays = (missionList) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysFilter);
    return missionList.filter(m => new Date(m.created_at) >= cutoffDate);
  };

  // Estatísticas filtradas por período
  const filteredStats = useMemo(() => {
    const filtered = filterByDays(missions);
    const newStats = { total: filtered.length };
    Object.keys(statusConfig).forEach(status => {
      newStats[status] = filtered.filter(m => m.status === status).length;
    });
    return newStats;
  }, [missions, daysFilter]);

  // Missões filtradas - ÚNICO FILTRO PARA TUDO (mapa, tabela, performance)
  const filteredMissions = useMemo(() => {
    let filtered = filterByDays(missions);
    
    // Filtro por tab/status
    if (activeTab !== 'all') {
      filtered = filtered.filter(m => m.status === activeTab);
    }
    
    // Filtro por busca
    if (searchTerm) {
      filtered = filtered.filter(m =>
        m.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.reason_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.vehicle_plate?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por cliente
    if (customerFilter !== 'all') {
      filtered = filtered.filter(m => m.customer_id === customerFilter);
    }

    // Filtro por fornecedor
    if (supplierFilter !== 'all') {
      filtered = filtered.filter(m => m.supplier_id === supplierFilter);
    }
    
    return filtered;
  }, [missions, activeTab, searchTerm, daysFilter, customerFilter, supplierFilter]);

  // Missões filtradas para o mapa - USA OS MESMOS FILTROS + seleção
  const filteredMapMissions = useMemo(() => {
    let filtered = filterByDays(mapMissions);
    
    // Filtrar pelo status da aba selecionada
    if (activeTab !== 'all') {
      filtered = filtered.filter(m => m.status === activeTab);
    }

    // Filtro por cliente
    if (customerFilter !== 'all') {
      filtered = filtered.filter(m => m.customer_id === customerFilter);
    }

    // Filtro por fornecedor
    if (supplierFilter !== 'all') {
      filtered = filtered.filter(m => m.supplier_id === supplierFilter);
    }

    // Se há uma missão selecionada, destacar apenas ela
    if (selectedMissionId) {
      const selected = filtered.find(m => m.id === selectedMissionId);
      if (selected) {
        return [{ ...selected, highlighted: true }];
      }
    }
    
    return filtered;
  }, [mapMissions, activeTab, daysFilter, customerFilter, supplierFilter, selectedMissionId]);

  // Acionamentos em andamento por duração
  const durationStats = useMemo(() => {
    const now = new Date();
    const emAndamento = missions.filter(m => m.status === 'em_andamento');
    
    let ate1h = 0;
    let de1a12h = 0;
    let acima12h = 0;
    
    emAndamento.forEach(m => {
      const start = new Date(m.started_at || m.supplier_arrived_at || m.created_at);
      const diffHours = (now - start) / (1000 * 60 * 60);
      
      if (diffHours <= 1) ate1h++;
      else if (diffHours <= 12) de1a12h++;
      else acima12h++;
    });
    
    return { total: emAndamento.length, ate1h, de1a12h, acima12h };
  }, [missions]);

  // Obter motivos únicos
  const uniqueReasons = useMemo(() => {
    const reasons = [...new Set(missions.map(m => m.reason_description).filter(Boolean))];
    return reasons;
  }, [missions]);

  const handleDaysChange = (value) => {
    const numValue = parseInt(value) || 30;
    setDaysFilter(Math.max(1, Math.min(365, numValue)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pl-14">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans' }}>
            Página Inicial
          </h1>
          <p className="text-slate-400 mt-1">Visão geral das missões de pronta resposta</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Secondary Status Icons (Validação, Aprovadas, Faturadas) */}
          <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 shadow-sm border border-slate-100">
            {secondaryStatuses.map((key) => {
              const config = statusConfig[key];
              const Icon = config.icon;
              const count = filteredStats[key] || 0;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(activeTab === key ? 'all' : key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                    activeTab === key 
                      ? 'bg-slate-900 text-white' 
                      : `${config.color} hover:opacity-80`
                  }`}
                  title={config.label}
                  data-testid={`status-icon-${key}`}
                >
                  <Icon className={`w-4 h-4 ${activeTab === key ? 'text-white' : config.iconColor}`} />
                  <span className="text-sm font-bold" style={{ fontFamily: 'JetBrains Mono' }}>{count}</span>
                </button>
              );
            })}
          </div>
          
          {/* Tenant Filter - Only for Master */}
          <TenantFilter />
          
          {/* Days Filter */}
          <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-2.5 shadow-sm border border-slate-100">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Últimos</span>
            <Input
              type="number"
              min="1"
              max="365"
              value={daysFilter}
              onChange={(e) => handleDaysChange(e.target.value)}
              className="w-16 h-8 text-center rounded-lg border-slate-200 text-sm font-medium"
              data-testid="days-filter-input"
            />
            <span className="text-sm text-slate-500">dias</span>
          </div>
          <Button 
            onClick={() => navigate('/missions/new')}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-11 px-6 btn-premium"
            data-testid="dashboard-new-mission-btn"
          >
            <Zap className="w-4 h-4 mr-2" />
            Nova Missão
          </Button>
        </div>
      </div>

      {/* Status Cards - Separated */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Total Card */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg border-l-4 border-l-slate-600 ${
            activeTab === 'all' 
              ? 'ring-2 ring-slate-900 shadow-lg' 
              : 'hover:scale-[1.02]'
          }`}
          onClick={() => setActiveTab('all')}
        >
          <CardContent className="p-4 flex flex-col items-center justify-center text-center">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
              activeTab === 'all' ? 'bg-slate-900' : 'bg-slate-100'
            }`}>
              <MapPin className={`w-6 h-6 ${activeTab === 'all' ? 'text-white' : 'text-slate-600'}`} />
            </div>
            <span className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
              {filteredStats.total}
            </span>
            <span className="text-xs text-slate-500 mt-1">Total</span>
          </CardContent>
        </Card>

        {/* Main Status Cards */}
        {mainStatuses.map((key) => {
          const config = statusConfig[key];
          const Icon = config.icon;
          const count = filteredStats[key] || 0;
          const isActive = activeTab === key;
          const borderColors = {
            criada: 'border-l-blue-500',
            aguardando_inicio: 'border-l-amber-500',
            em_andamento: 'border-l-purple-500',
            finalizada: 'border-l-green-500',
            cancelada: 'border-l-red-500'
          };
          
          return (
            <Card 
              key={key}
              className={`cursor-pointer transition-all hover:shadow-lg border-l-4 ${borderColors[key]} ${
                isActive 
                  ? 'ring-2 ring-slate-900 shadow-lg' 
                  : count === 0 ? 'opacity-60' : 'hover:scale-[1.02]'
              }`}
              onClick={() => setActiveTab(isActive ? 'all' : key)}
              data-testid={`status-card-${key}`}
            >
              <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                  isActive ? 'bg-slate-900' : config.color.replace('text-', 'bg-').split(' ')[0]
                }`}>
                  <Icon className={`w-6 h-6 ${isActive ? 'text-white' : config.iconColor}`} />
                </div>
                <span className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                  {count}
                </span>
                <span className="text-xs text-slate-500 mt-1">{config.label}</span>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Interactive Map */}
        <Card className="lg:col-span-2 premium-card border-0 overflow-visible">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
                  Mapa de Missões {activeTab !== 'all' && `- ${statusConfig[activeTab]?.label}`}
                </CardTitle>
                <p className="text-sm text-slate-400 mt-1">
                  {filteredMapMissions.length} missões {activeTab !== 'all' ? `(${statusConfig[activeTab]?.label})` : ''} nos últimos {daysFilter} dias
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-0">
            <MissionsMap 
              missions={filteredMapMissions} 
              selectedStatus={activeTab}
              onMissionClick={(mission) => navigate(`/missions/${mission.id}`)}
              className="h-[400px] rounded-xl overflow-hidden"
            />
          </CardContent>
        </Card>

        {/* Acionamentos em Andamento por Duração */}
        <Card className="premium-card border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
              Acionamentos em Andamento
            </CardTitle>
            <p className="text-sm text-slate-400">Tempo de duração dos acionamentos ativos</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-5 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-green-700">Até 1 hora</span>
                  <Clock className="w-4 h-4 text-green-500" />
                </div>
                <p className="text-3xl font-semibold text-green-800" style={{ fontFamily: 'JetBrains Mono' }}>
                  {durationStats.ate1h}
                </p>
              </div>
              
              <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-amber-700">De 1 a 12 horas</span>
                  <Clock className="w-4 h-4 text-amber-500" />
                </div>
                <p className="text-3xl font-semibold text-amber-800" style={{ fontFamily: 'JetBrains Mono' }}>
                  {durationStats.de1a12h}
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-gradient-to-br from-red-50 to-rose-50 border border-red-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-red-700">Acima de 12 horas</span>
                  <AlertCircle className="w-4 h-4 text-red-500" />
                </div>
                <p className="text-3xl font-semibold text-red-800" style={{ fontFamily: 'JetBrains Mono' }}>
                  {durationStats.acima12h}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters Row */}
      <Card className="premium-card border-0">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filtros:</span>
            </div>
            
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar código, placa, cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 rounded-xl border-slate-200 bg-slate-50"
                data-testid="search-input"
              />
            </div>

            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="w-44 h-10 rounded-xl border-slate-200 bg-slate-50">
                <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Todos Clientes</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-44 h-10 rounded-xl border-slate-200 bg-slate-50">
                <Users className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Fornecedor" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Todos Fornecedores</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(searchTerm || customerFilter !== 'all' || supplierFilter !== 'all' || selectedMissionId) && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  setSearchTerm('');
                  setCustomerFilter('all');
                  setSupplierFilter('all');
                  setSelectedMissionId(null);
                }}
                className="text-slate-500 hover:text-slate-900"
              >
                Limpar Filtros
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Missions Table */}
      <Card className="premium-card border-0">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
              {activeTab === 'all' ? 'Todas as Missões' : statusConfig[activeTab]?.label || 'Missões'} 
              <Badge className="ml-2 bg-slate-100 text-slate-600">{filteredMissions.length}</Badge>
              {selectedMissionId && (
                <Badge className="ml-2 bg-blue-100 text-blue-600">
                  1 selecionada
                </Badge>
              )}
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-lg"
              onClick={() => navigate('/missions')}
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-80">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Motivo</th>
                  <th>Cliente</th>
                  <th>Fornecedor</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredMissions.slice(0, 15).map((mission) => {
                  const config = statusConfig[mission.status];
                  const isSelected = selectedMissionId === mission.id;
                  return (
                    <tr 
                      key={mission.id} 
                      data-testid={`mission-row-${mission.id}`}
                      onClick={() => setSelectedMissionId(isSelected ? null : mission.id)}
                      className={`cursor-pointer transition-all ${isSelected ? 'bg-blue-50 ring-2 ring-blue-200' : 'hover:bg-slate-50'}`}
                    >
                      <td>
                        <span className="font-mono text-sm font-medium text-slate-900">{mission.code}</span>
                      </td>
                      <td className="text-slate-500 max-w-[150px] truncate">{mission.reason_description}</td>
                      <td className="text-slate-500">{mission.customer_name || '-'}</td>
                      <td className="text-slate-500">{mission.supplier_name || '-'}</td>
                      <td>
                        <Badge className={`${config?.color || 'bg-slate-100'} border rounded-full px-3`}>
                          {config?.label || mission.status}
                        </Badge>
                      </td>
                      <td className="text-slate-400 text-sm font-mono">
                        {new Date(mission.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/missions/${mission.id}`)}
                          className="rounded-lg hover:bg-slate-100"
                        >
                          <ArrowUpRight className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollArea>
          {filteredMissions.length > 15 && (
            <div className="mt-4 text-center">
              <Button 
                variant="outline" 
                onClick={() => navigate('/missions')} 
                className="rounded-xl border-slate-200"
              >
                Ver todas ({filteredMissions.length})
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
